import React from "react";
import { motion } from "framer-motion";
import LoginIllustration from "./LoginIllustration";
import ForgotIllustration from "./ForgotIllustration";
import OtpIllustration from "./OtpIllustration";

const images = {
  Login: "/assets/images/login.png",
  ForgotPassword: "/assets/images/forgotPassword.png",
  ResetPassword: "/assets/images/resetPassword.png",
  Otp: "/assets/images/otp.png",
  Authentication: "/assets/images/otp.png",
};

const AuthLayout = ({ children, title }) => {
  const imageSrc = images[title];

  const renderIllustration = () => {
    switch (title) {
      case "Login":
        return <LoginIllustration />;
      case "ForgotPassword":
      case "ResetPassword":
        return <ForgotIllustration />;
      case "Otp":
      case "Authentication":
        return <OtpIllustration />;
      default:
        return (
          <motion.img
            key={imageSrc}
            src={imageSrc}
            alt={title}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, type: "spring" }}
            className="relative z-10 w-full max-w-lg object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500 ease-in-out"
          />
        );
    }
  };

  return (
    <div className="min-h-screen w-full bg-bgColor flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary/10 rounded-full blur-3xl pointer-events-none"></div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-7xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:flex-row min-h-[600px] lg:h-[800px]"
      >
        {/* Left Side - Form Content */}
        <div className="w-full lg:w-1/2 p-8 sm:p-12 lg:p-16 flex flex-col justify-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-8"
          >
            <img src="/assets/images/logo.png" alt="Trackmydoc" className="h-12 w-auto object-contain" />
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="w-full max-w-md mx-auto lg:mx-0 flex-1 flex flex-col justify-center"
          >
            {children}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8 text-center lg:text-left text-xs text-gray-400"
          >
            &copy; {new Date().getFullYear()} Trackmydoc. All rights reserved.
          </motion.div>
        </div>

        {/* Right Side - Image/Illustration */}
        <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-primary/5 to-secondary/5 items-center justify-center p-12 relative overflow-hidden">
          {/* Decorative shapes */}
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3], 
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity,
              ease: "easeInOut" 
            }}
            className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary/20 rounded-full blur-3xl"
          />
          <motion.div 
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.6, 0.3], 
            }}
            transition={{ 
              duration: 10, 
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1 
            }}
            className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-secondary/20 rounded-full blur-3xl"
          />
          
          {renderIllustration()}
        </div>
      </motion.div>
    </div>
  );
};

export default AuthLayout;
